import { AstraHoverCard, AstraParagraph } from '~/astra'
import { LoadingBarListArray } from '~/components/BarList'

type BarProps = {
    color: string
    text: string
    valueText: string
    width: number
    chartWidth: number
}

const Bar = ({ color, text, valueText, width, chartWidth }: BarProps) => {
    const gridItemBase = 'col-start-1 row-start-1 rounded'
    const overlayClasses = `${gridItemBase} col-span-full px-2 py-1`

    return (
        <div className="flex h-7 items-center justify-between gap-x-6">
            <div className="grid h-full w-full flex-1 grid-rows-1" style={{ gridTemplateColumns: `repeat(${chartWidth}, minmax(0, 1fr))` }}>
                <div className={gridItemBase} style={{ gridColumnEnd: `span ${width}`, background: color }} />
                <div className={overlayClasses}>
                    <AstraParagraph variant="body">{text}</AstraParagraph>
                </div>
            </div>
            <AstraParagraph className="flex-shrink" variant="body">
                {valueText}
            </AstraParagraph>
        </div>
    )
}

type LegendProps = {
    bookedSurgeries: number
    availableSurgeries: number
}
const Legend = ({ bookedSurgeries, availableSurgeries }: LegendProps) => (
    <div className="flex flex-col items-start gap-y-2">
        <div className="flex gap-x-2">
            <div className="h-3 w-3 rounded bg-[#C1D0FF]" />
            <AstraParagraph variant="body">{bookedSurgeries} bestilte operasjoner</AstraParagraph>
        </div>
        <div className="flex gap-x-2">
            <div className="h-3 w-3 rounded border-2 border-[#C1D0FF]" />
            <AstraParagraph variant="body">{availableSurgeries} tilgjengelig kapasitet</AstraParagraph>
        </div>
    </div>
)

const ProgressBar = ({ color, text, valueText, width, chartWidth, progress }: BarProps & { progress: number }) => {
    const gridItemBase = 'col-start-1 row-start-1 rounded'
    const overlayClasses = `${gridItemBase} col-span-full px-2 py-1`

    return (
        <AstraHoverCard placement="bottom-start" hoverContent={<Legend bookedSurgeries={progress} availableSurgeries={width - progress} />}>
            <div className="flex h-7 items-center justify-between gap-x-6">
                <div className="grid h-full w-full flex-1 grid-rows-1" style={{ gridTemplateColumns: `repeat(${chartWidth}, minmax(0, 1fr))` }}>
                    <div
                        className={gridItemBase}
                        style={{
                            gridColumnEnd: `span ${width}`,
                            backgroundColor: 'transparent',
                            border: `2px solid ${color}`,
                        }}
                    />
                    <div
                        className={gridItemBase}
                        style={{
                            gridColumnEnd: `span ${progress}`,
                            backgroundColor: color,
                        }}
                    />
                    <div className={overlayClasses}>
                        <AstraParagraph variant="body">{text}</AstraParagraph>
                    </div>
                </div>
                <AstraParagraph className="flex-shrink" variant="body">
                    {valueText}
                </AstraParagraph>
            </div>
        </AstraHoverCard>
    )
}

type Props = {
    isLoading: boolean
    initialWaitingList: number
    totalPredictedSurgeries: number
    bookedSurgeries: number
    predictedIncrease: number
    finalWaitingList: number
}

export const PredictedWaitingListBars = ({
    isLoading,
    initialWaitingList,
    totalPredictedSurgeries,
    bookedSurgeries,
    predictedIncrease,
    finalWaitingList,
}: Props) => {
    const biggestTotal = Math.max(initialWaitingList, totalPredictedSurgeries, predictedIncrease, finalWaitingList)

    return (
        <div className="flex flex-col gap-y-3">
            <div className="flex items-baseline justify-between gap-2">
                <AstraParagraph variant="body" weight="medium">
                    Venteliste og forventet aktivitet
                </AstraParagraph>
                <AstraParagraph variant="body" weight="medium">
                    pasienter
                </AstraParagraph>
            </div>
            {isLoading ? (
                <LoadingBarListArray length={4} />
            ) : (
                <div className="flex flex-col gap-2">
                    <Bar
                        color="#9DDDE7"
                        text="Estimert venteliste starten av perioden"
                        valueText={initialWaitingList.toString()}
                        width={initialWaitingList}
                        chartWidth={biggestTotal}
                    />
                    <Bar
                        color="#FFDFB5"
                        text="Innkommende pasienter"
                        valueText={`+ ${predictedIncrease}`}
                        width={predictedIncrease}
                        chartWidth={biggestTotal}
                    />
                    <ProgressBar
                        color="#C1D0FF"
                        text="Operasjonskapasitet"
                        valueText={`- ${totalPredictedSurgeries}`}
                        width={totalPredictedSurgeries}
                        chartWidth={biggestTotal}
                        progress={bookedSurgeries}
                    />
                    <Bar
                        color="#DEF7F9"
                        text="Estimert venteliste slutten av perioden"
                        valueText={`= ${finalWaitingList}`}
                        width={finalWaitingList}
                        chartWidth={biggestTotal}
                    />
                </div>
            )}
        </div>
    )
}
